import { createLogger, Logger, LoggerLevel, HttpHeaders } from '@quickplay/foundation';
import { PlatformAuthorizer } from '@quickplay/platform-authorizer';
import { StreamConcurrencyService } from '../StreamConcurrencyService';
import { HeartbeatConfiguration } from '../HeartbeatConfiguration';
import { HeartbeatBuilder } from '../HeartbeatBuilder';
import { HeartbeatManager } from '../HeartbeatManager';
import { HeartbeatService } from '../HeartbeatService';
import { createHeartbeatManager } from './HeartbeatManagerImpl';
import { OverflowEventMode } from '../OverflowMode';

const TAG_NAME = 'FL_HEARTBEAT';

export function createHeartbeatBuilder(
  deviceId: string,
  contentId: string,
  configuration: HeartbeatConfiguration,
  platformAuthorizer: PlatformAuthorizer,
): HeartbeatBuilder {
  const _deviceId: string = deviceId;
  const _contentId: string = contentId;
  const _configuration: HeartbeatConfiguration = configuration;
  const _platformAuthorizer: PlatformAuthorizer = platformAuthorizer;
  let _heartbeatToken: string | undefined;
  let _headers: HttpHeaders | undefined;
  let _streamConcurrencyService: StreamConcurrencyService | undefined;
  let _heartbeatService: HeartbeatService | undefined;
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let _liveStartTime: string | undefined;
  let _liveEndTime: string | undefined;
  let _liveEventType: string | undefined;
  let _overflowEventMode: OverflowEventMode | undefined;
  let _catalogType: string | undefined;
  let _primaryContentId: string | undefined;
  let _live360StartTime: string | undefined;
  let _live360EndTime: string | undefined;
  let _pgmStartTime: string | undefined;
  let _pgmEndTime: string | undefined;
  let _pgmBlackout: boolean | undefined;

  return {
    get deviceId(): string {
      return _deviceId;
    },

    get contentId(): string {
      return _contentId;
    },

    get heartbeatToken(): string | undefined {
      return _heartbeatToken;
    },

    get configuration(): HeartbeatConfiguration {
      return _configuration;
    },

    get streamConcurrencyService(): StreamConcurrencyService | undefined {
      return _streamConcurrencyService;
    },

    get heartbeatService(): HeartbeatService | undefined {
      return _heartbeatService;
    },

    get platformAuthorizer(): PlatformAuthorizer {
      return _platformAuthorizer;
    },

    get headers(): HttpHeaders | undefined {
      return _headers;
    },

    get logger(): Logger {
      return defaultLogger;
    },

    get liveStartTime(): string | undefined {
      return _liveStartTime;
    },

    get liveEndTime(): string | undefined {
      return _liveEndTime;
    },

    get liveEventType(): string | undefined {
      return _liveEventType;
    },

    get overflowEventMode(): OverflowEventMode | undefined {
      return _overflowEventMode;
    },

    get catalogType(): string | undefined {
      return _catalogType;
    },

    get primaryContentId(): string | undefined {
      return _primaryContentId;
    },

    get live360StartTime(): string | undefined {
      return _live360StartTime;
    },

    get live360EndTime(): string | undefined {
      return _live360EndTime;
    },

    get pgmStartTime(): string | undefined {
      return _pgmStartTime;
    },

    get pgmEndTime(): string | undefined {
      return _pgmEndTime;
    },

    get pgmBlackout(): boolean | undefined {
      return _pgmBlackout;
    },

    /**
     * @method Method setHeartbeatToken
     * @param token The heartbeat token received from playbak authorization
     */
    setHeartbeatToken(token?: string): HeartbeatBuilder {
      _heartbeatToken = token;
      return this;
    },

    /**
     * @method Method setHeaders
     * @param headers The custom headers to be sent to the heartbeat service
     */
    setHeaders(headers: HttpHeaders): HeartbeatBuilder {
      _headers = headers;
      return this;
    },

    /**
     * @method Method streamConcurrencyService
     * @param {array} service An instance of stream concurrency service which reserves and un-reserves
     * a stream
     */
    setStreamConcurrencyService(service: StreamConcurrencyService): HeartbeatBuilder {
      _streamConcurrencyService = service;
      return this;
    },

    /**
     * @method Method heartbeatService
     * @param {array} service An instance of heartbeat service which ping the Firstlight Platform to keep the stream active
     */
    setHeartbeatService(service: HeartbeatService): HeartbeatBuilder {
      _heartbeatService = service;
      return this;
    },

    setLogger(logger: Logger): HeartbeatBuilder {
      defaultLogger = logger;
      return this;
    },

    /**
     * @method setLiveStartTime Sets the liveStarttime for the heartbeat.
     * @param liveStartTime specifies the starttime of the live program/event strat time.
     * It is string in ISO8601 format.
     */
    setLiveStartTime(liveStartTime?: string): HeartbeatBuilder {
      _liveStartTime = liveStartTime;
      return this;
    },

    /**
     * @method setLiveEndTime Sets the liveEndtime for the heartbeat.
     * @param liveEndTime specifies the endtime of the live program/event end time.
     * It is string in ISO8601 format.
     */
    setLiveEndTime(liveEndTime?: string): HeartbeatBuilder {
      _liveEndTime = liveEndTime;
      return this;
    },

    /**
     * @method setLiveEventType Sets the liveEventType for the heart beat.
     * @param liveEventType specifies overflow event of the live
     * It is string in ISO8601 format.
     */
    setLiveEventType(liveEventType?: string): HeartbeatBuilder {
      _liveEventType = liveEventType;
      return this;
    },

    /**
     * @method setOverflowEventMode Sets the overflowEventMode for the heart beat.     *
     * @typedef {{
     * PRE_EVENT = 'preEvent',
     * EVENT = 'event'
     * POST_EVENT = 'postEvent'
     * }}
     * [{@link OverflowEventMode}]
     */
    setOverflowEventMode(overflowEventMode?: OverflowEventMode): HeartbeatBuilder {
      _overflowEventMode = overflowEventMode;
      return this;
    },

    /**
     * @method setCatalogType Sets the catalog type
     * @param catalogType specifies the catalog type
     */
    setCatalogType(catalogType?: string): HeartbeatBuilder {
      _catalogType = catalogType;
      return this;
    },

    /**
     * @method setPrimaryContentId Sets the primary content id
     * @param prid specifies the primary content id
     */
    setPrimaryContentId(prid?: string): HeartbeatBuilder {
      _primaryContentId = prid;
      return this;
    },

    setLive360StartTime(startTime?: string): HeartbeatBuilder {
      _live360StartTime = startTime;
      return this;
    },

    setLive360EndTime(endTime?: string): HeartbeatBuilder {
      _live360EndTime = endTime;
      return this;
    },

    setProgramStartTime(startTime?: string): HeartbeatBuilder {
      _pgmStartTime = startTime;
      return this;
    },

    setProgramEndTime(endTime?: string): HeartbeatBuilder {
      _pgmEndTime = endTime;
      return this;
    },

    setProgramBlackout(isBlackout?: boolean): HeartbeatBuilder {
      _pgmBlackout = isBlackout;
      return this;
    },

    build(): HeartbeatManager {
      return createHeartbeatManager(this);
    },
  };
}
